import { z } from 'zod/v3';
import { ok } from 'neverthrow';
import {
  ApplicableContext,
  BaseAction,
  ExecuteContext,
  useWatchableValue,
} from '@cotera/client/v0/actions/framework';
import { Assert } from '@cotera/utilities';
import { DataGridModel } from '../model';
import { ModalItem, ModalPageView } from '@cotera/client/app/components/app';
import { DetailView } from '../detail-view';
import React, { useState } from 'react';
import { Relation } from '@cotera/era';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@cotera/client/v0/components/ui/tabs';

type Params = {};

export class OpenDetailViewAction extends BaseAction<Params, { t: string }[]> {
  title = 'Open cell detail view';
  description = 'Open a cell detail view';
  inputSchema = z.object({});

  constructor(
    private readonly modalManager: {
      push: (
        child: ModalItem & {
          onClose?: () => void;
        }
      ) => void;
    }
  ) {
    super();
  }

  applicable(context: ApplicableContext): boolean {
    const model = context.targetedModelOfType(DataGridModel);
    return model !== null && model.selectedRows.snapshot().size > 0;
  }

  async execute(_: {}, context: ExecuteContext) {
    const dataGrid = context.targetedModelOfType(DataGridModel);

    Assert.assert(dataGrid !== null, 'Data grid model not found');

    const rel = dataGrid.rel;
    const selectedCount = dataGrid.selectedRows.snapshot().size;

    this.modalManager.push({
      view: () => (
        <ModalPageView
          //0 indexed, but we want to show 1 indexed
          title={<Title dataGrid={dataGrid} />}
          className="px-4 py-4"
        >
          <View rel={rel} dataGrid={dataGrid} />
        </ModalPageView>
      ),
      // Use larger fixed size for multi-row to prevent width changes when switching tabs
      size: selectedCount > 1 ? 'lg' : 'auto',
    });

    return ok({});
  }
}

// Helper to truncate long PK values
const truncatePk = (pk: unknown, maxLength: number = 20): string => {
  const str = String(pk);
  if (str.length <= maxLength) {
    return str;
  }
  return `${str.slice(0, maxLength)}...`;
};

const Title: React.FC<{
  dataGrid: DataGridModel;
}> = ({ dataGrid }) => {
  const selectedRows = useWatchableValue(dataGrid.selectedRows);
  const selectedCount = selectedRows.size;

  if (selectedCount === 0) {
    return 'No rows selected';
  }

  if (selectedCount === 1) {
    const rowIndex = Array.from(selectedRows)[0]!;
    const data = dataGrid.data.snapshot();
    const rowData = data?.row(rowIndex).toArray()[0] ?? null;
    const pk = rowData?.['__pk'];

    if (pk !== null && pk !== undefined && pk !== '') {
      return `Details for row #${rowIndex + 1} (pk: ${truncatePk(pk)})`;
    }
    return `Details for row #${rowIndex + 1}`;
  }

  return `Details for ${selectedCount} rows`;
};

const SingleRowView: React.FC<{
  rel: Relation;
  dataGrid: DataGridModel;
  rowIndex: number;
  rowData: Record<string, unknown>;
}> = ({ rel, dataGrid, rowIndex, rowData }) => {
  const focusedCell = useWatchableValue(dataGrid.focusedCell);
  const columnKey = focusedCell?.row === rowIndex ? focusedCell.columnKey : '';

  return (
    <DetailView
      col={columnKey}
      rowData={rowData}
      rel={rel}
      rowIndex={rowIndex}
      onKeyDown={(e) => {
        const { row, col } = dataGrid.focusedCell.snapshot() ?? {
          row: rowIndex,
          col: 0,
        };
        const columns = dataGrid.columns.snapshot();
        const rows = dataGrid.totalLoadedRows.snapshot();
        let newRow = row;
        let newCol = col;

        const select = () => {
          dataGrid.focusedCell.set({
            row: newRow,
            col: newCol,
            columnKey: columns[newCol]!.key,
            rowData: dataGrid.data.snapshot()!.row(newRow).toArray()[0]!,
          });
        };

        switch (e.key) {
          case 'ArrowUp':
            e.preventDefault();
            newRow = Math.max(0, row - 1);
            select();
            break;
          case 'ArrowDown':
            e.preventDefault();
            newRow = Math.min(rows - 1, row + 1);
            select();
            break;
          case 'ArrowLeft':
            e.preventDefault();
            newCol = Math.max(0, col - 1);
            select();
            break;
          case 'ArrowRight':
            e.preventDefault();
            newCol = Math.min(columns.length - 1, col + 1);
            select();
            break;
          default:
            return;
        }
      }}
      renderer={dataGrid.renderer?.detailViewCell}
    />
  );
};

const View: React.FC<{
  rel: Relation;
  dataGrid: DataGridModel;
}> = ({ rel, dataGrid }) => {
  const selectedRows = useWatchableValue(dataGrid.selectedRows);
  const sortedSelectedRows = Array.from(selectedRows).sort((a, b) => a - b);
  const [activeTab, setActiveTab] = useState<string>(
    `row-${sortedSelectedRows[0]}`
  );

  // Get row data for each selected row
  const getRowData = (rowIndex: number): Record<string, unknown> => {
    const data = dataGrid.data.snapshot();
    if (data === null) {
      return {};
    }
    return data.row(rowIndex).toArray()[0] ?? {};
  };

  // Single row - use original behavior
  if (sortedSelectedRows.length === 1) {
    const rowIndex = sortedSelectedRows[0]!;
    return (
      <SingleRowView
        rel={rel}
        dataGrid={dataGrid}
        rowIndex={rowIndex}
        rowData={getRowData(rowIndex)}
      />
    );
  }

  // Multiple rows - show tabs
  return (
    <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
      <TabsList className="mb-4 w-full justify-start overflow-x-auto">
        {sortedSelectedRows.map((rowIndex) => (
          <TabsTrigger key={rowIndex} value={`row-${rowIndex}`}>
            Row #{rowIndex + 1}
          </TabsTrigger>
        ))}
      </TabsList>
      {sortedSelectedRows.map((rowIndex) => (
        <TabsContent
          key={rowIndex}
          value={`row-${rowIndex}`}
          className="w-full"
        >
          <SingleRowView
            rel={rel}
            dataGrid={dataGrid}
            rowIndex={rowIndex}
            rowData={getRowData(rowIndex)}
          />
        </TabsContent>
      ))}
    </Tabs>
  );
};
